import { GroupBase, StylesConfig } from "react-select";
import {
  IDeliveryMethodEmail,
  IDeliveryMethodItem,
  IDeliveryTypeOptions,
  ITypeOptionsEmailDelivery,
  ITypeOptionsSyslogDelivery,
  SubscriptionHashes
} from "./@types";
import { MenuItem, MenuItems } from "../../components/@types";
import { DELIVERY_METHOD_TYPE } from "./const";
import { pluralize } from "../../utils";
import { consts } from "../../shim/jsbridge";
import { show } from "../../notifications/notifications";

export const getItemsAsMenuOptions = <T extends Record<string, any>>(
  data: Array<T> | undefined,
  keys: {
    value: keyof T;
    label: keyof T;
  }
): MenuItems =>
  !data
    ? []
    : data.map(item => ({
        value: String(item[keys.value]),
        label: String(item[keys.label])
      }));

export const formatArrayDataAsMenuOptions = (
  values: string[] | undefined,
  labelFormatter?: (value: string) => string
): MenuItems =>
  !values
    ? []
    : values.map(value => ({
        value,
        label: labelFormatter ? labelFormatter(value) : value
      }));

export const getDeliveryMethodsLabelList = (
  data: Array<IDeliveryMethodItem>
): Array<string> => {
  const labels = data.map(
    deliveryMethod =>
      DELIVERY_METHOD_TYPE[
        deliveryMethod.type as keyof typeof DELIVERY_METHOD_TYPE
      ].label
  );
  return [...new Set(labels)];
};

export const getDefaultDeliveryTypeOptions = (
  deliveryType: keyof typeof DELIVERY_METHOD_TYPE
): IDeliveryTypeOptions => {
  if (deliveryType === DELIVERY_METHOD_TYPE.email.name) {
    return DELIVERY_METHOD_TYPE.email
      .defaultOptions as ITypeOptionsEmailDelivery;
  }

  if (deliveryType === DELIVERY_METHOD_TYPE.syslog.name) {
    return DELIVERY_METHOD_TYPE.syslog
      .defaultOptions as ITypeOptionsSyslogDelivery;
  }

  throw new Error(`Unsupported delivery type: ${deliveryType}`);
};

const isEmailTypeOptions = (options: ITypeOptionsEmailDelivery) =>
  Array.isArray(options.to);

export const setupEmailFieldValues = (data: IDeliveryMethodEmail) => {
  const userEmail = window.tcbase.userEmail;
  if (data.type !== DELIVERY_METHOD_TYPE.email.name || !userEmail) return data;

  if (!isEmailTypeOptions(data.type_options)) return data;

  // look for the user's email in the delivery email addresses and remove it if there
  if (
    data.type_options.to.some(
      (email: string) =>
        email.toLowerCase().trim() === userEmail.toLowerCase().trim()
    )
  ) {
    const emails = data.type_options.to.filter(
      email => email.toLowerCase().trim() !== userEmail.toLowerCase().trim()
    );

    return {
      ...data,
      type_options: {
        to: emails,
        to_user: true
      }
    };
  }
  return data;
};

export const checkForUserEmail = (data: IDeliveryMethodEmail) => {
  if (data.type !== DELIVERY_METHOD_TYPE.email.name) return data;

  if (!data.type_options.to_user) {
    return data;
  }
  const updatedEmails = [
    window.tcbase.userEmail,
    ...(data.type_options.to || [])
  ];
  return {
    ...data,
    type_options: { to: updatedEmails }
  };
};

export const formatHashLabel = (label: string) =>
  `${label.slice(0, 5)}...${label.slice(-3)}`;

export const formatGridDeliveryColumnValue = (data: IDeliveryMethodItem) => {
  const isEmailDelivery = data.type === DELIVERY_METHOD_TYPE.email.name;
  if (!isEmailDelivery)
    return DELIVERY_METHOD_TYPE[data.type as keyof typeof DELIVERY_METHOD_TYPE]
      .label;

  const emails = (data.type_options as ITypeOptionsEmailDelivery).to;
  const hasUserEmail = emails.includes(window.tcbase.userEmail);
  const recipientText = (() => {
    if (emails.length === 1 && hasUserEmail) return "You";

    const count = hasUserEmail ? emails.length - 1 : emails.length;
    const recipientPart = pluralize("Recipient", count);
    return hasUserEmail
      ? `You, ${count} Other ${recipientPart}`
      : `${count} ${recipientPart}`;
  })();

  return `${DELIVERY_METHOD_TYPE.email.label} (${recipientText})`;
};

export const shouldUseSampleAvailableAlertType = (
  hashes: SubscriptionHashes
): boolean =>
  hashes?.length ? hashes?.filter(hash => !hash.available).length > 0 : false;

export const handleAlertLimitCheck = (
  totalEventCount: number,
  callback: () => void,
  selectedEventCount = 0
) => {
  const maxSubscriptions = consts.max_number_alert_subscriptions;
  const adjustedTotal = totalEventCount + selectedEventCount;
  if (adjustedTotal > maxSubscriptions) {
    show({
      level: "warning",
      sticky: true,
      message: `Alert events limit (${maxSubscriptions}) reached. \
          You must remove some of the existing alert events to be able to create more.`
    });
  } else {
    callback();
  }
};

export const getUniqueHashes = (
  newUniqueHashes: Array<string>,
  existingHashes?: Array<string>
) => {
  if (!existingHashes) return newUniqueHashes;
  return newUniqueHashes.filter(
    (hash: string) => !existingHashes.includes(hash)
  );
};

export const splitHashesByUnique = (
  newUniqueHashes: Array<string>,
  existingHashes?: Array<string>
) => {
  if (!existingHashes)
    return { uniqueHashes: newUniqueHashes, duplicateHashes: [] };

  const uniqueHashes: string[] = [];
  const duplicateHashes: string[] = [];

  newUniqueHashes.forEach(hash => {
    if (existingHashes.includes(hash)) {
      duplicateHashes.push(hash);
    } else {
      uniqueHashes.push(hash);
    }
  });

  return { uniqueHashes, duplicateHashes };
};

export const getSelectStyle = (
  hasError: boolean,
  isDisabled = false
): StylesConfig<MenuItem, true, GroupBase<MenuItem>> => ({
  control: base => ({
    ...base,
    borderColor: hasError ? "#d00000" : base.borderColor
  }),
  dropdownIndicator: base => ({
    ...base,
    color: isDisabled ? "#ccc" : "#343a40"
  }),
  clearIndicator: base => ({
    ...base,
    color: "#343a40"
  })
});
